/**
 * File: src/pages/Dashboard/chat/ChatMenu.js
 * Description: Chat sidebar listing conversations with search, filters, pins,
 *              and per-chat actions. Integrates with network and Redux actions.
 * Dependencies: React, Ant Design, custom components, network utils.
 * Guidelines:
 *  - Keep UI strings localizable.
 *  - Document non-trivial helpers and event handlers.
 * Created: 2025-09-23
 * Last Updated: 2025-09-23
 */
 
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Badge, Input, Select } from 'antd';
import AvatarStatus from 'components/shared-components/AvatarStatus';
import { COLOR_1 } from 'constants/ChartConstant';
import { SearchOutlined } from '@ant-design/icons';
import { Spin, Tabs, Dropdown, Menu, message } from 'antd';
import globalConstant from 'constants/globalConstant';
import CustomBadge from 'components/CustomBadge';
import './ChatMenu.css';
import { Scrollbars } from 'react-custom-scrollbars';
import { EllipsisOutlined } from '@ant-design/icons';
import network from 'network/network';

const { TabPane } = Tabs;

/**
 * ChatMenu Component
 *
 * Sidebar for chat conversations with search, tab filters, pinning, and actions.
 *
 * @param {Object} props
 * @param {Array} props.chatList - Conversations to display
 * @param {boolean} props.isLoading - Loading state for chat list
 * @param {Function} props.resetUnreadCountactions - Resets unread count for a chat
 * @param {Function} props.onChangeChatIndex - Changes selected chat
 * @param {string|number} props.activeNumber - Active chat/customer number
 * @param {Array} props.filterOptions - Options for filter dropdowns
 * @param {boolean} props.filterOptionsLoading - Loading state for filters
 * @param {Function} props.selectChatFilterOptionsAction - Loads filter options
 * @param {Function} props.changeConversationActivityStatusAction - Changes conversation status
 * @param {Function} props.startManualChatAction - Starts manual chat
 * @param {Function} props.onChatListRefresh - Refreshes chat list
 */
const ChatMenu = ({ 
	chatList, 
	isLoading, 
	resetUnreadCountactions,
	onChangeChatIndex, 
	activeNumber,
	filterOptions,
	filterOptionsLoading,
	selectChatFilterOptionsAction,
	changeConversationActivityStatusAction,
	startManualChatAction,
	onChatListRefresh
}) => {

	const [searchText, setSearchText] = useState("");
	const [activeTab, setActiveTab] = useState("2");
	const [pinnedChats, setPinnedChats] = useState([]); // Pinned chat numbers
	const [selectedFilter, setSelectedFilter] = useState(null);

	/**
	 * Formats chat item's time label.
	 * Uses backend provided date/time when available; falls back gracefully.
	 *
	 * @param {Object} item - Chat item containing lastMessageDate/Time or time
	 * @returns {string} Human-readable time or date
	 */
	const formatChatTime = useCallback((item) => {
		try {
			const today = new Date();
			const todayStr = today.toLocaleDateString('tr-TR'); // dd.MM.yyyy formatında bugünün tarihi
			
			// lastMessageDate (tarih) ve lastMessageTime (saat) backend'den geliyor
			if (item.lastMessageDate) {
				let messageDate = item.lastMessageDate;
				let messageTime = item.lastMessageTime; // Backend'den HH:mm formatında
				
				// Backend'den gelen tarih formatını kontrol et
				// ToShortDateString() genelde dd.MM.yyyy veya MM/dd/yyyy formatında gelir
				let parsedMessageDate;
				
				if (typeof messageDate === 'string') {
					// Türkiye locale'i için dd.MM.yyyy formatı beklenir
					if (messageDate.includes('.')) {
						// dd.MM.yyyy formatı
						const [day, month, year] = messageDate.split('.');
						parsedMessageDate = `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
					} else if (messageDate.includes('/')) {
						// MM/dd/yyyy veya dd/MM/yyyy formatı
						const parts = messageDate.split('/');
						if (parts.length === 3) {
							// Türk formatına çevir: dd.MM.yyyy
							parsedMessageDate = `${parts[1].padStart(2, '0')}.${parts[0].padStart(2, '0')}.${parts[2]}`;
						}
					} else {
						// Diğer formatlar için direkt parse et
						const date = new Date(messageDate);
						parsedMessageDate = date.toLocaleDateString('tr-TR');
					}
				}
				

				
				if (parsedMessageDate === todayStr) {
					// Bugünkü mesaj - backend'den gelen gerçek saati kullan
					if (messageTime && messageTime.match(/^\d{1,2}:\d{2}$/)) {
						// Backend'den HH:mm formatında saat geldi
						return messageTime;
					} else if (item.time && item.time.match(/^\d{1,2}:\d{2}([AP]M)?$/i)) {
						// Fallback: time alanından saat al
						return item.time;
					} else {
						// Son fallback: şu anki saati göster
						const now = new Date();
						const hours = now.getHours().toString().padStart(2, '0');
						const minutes = now.getMinutes().toString().padStart(2, '0');
						return `${hours}:${minutes}`;
					}
				} else {
					// Geçmiş tarih - tam tarihi göster (dd/MM/yyyy formatında)
					if (parsedMessageDate && parsedMessageDate.includes('.')) {
						// dd.MM.yyyy'yi dd/MM/yyyy'ye çevir
						return parsedMessageDate.replace(/\./g, '/');
					}
					return parsedMessageDate || messageDate;
				}
			}
			
			// Fallback: time alanını kullan
			if (item.time) {
				// Saat formatı (14:30, 4:16PM) - bugünkü mesaj olarak kabul et
				if (item.time.match(/^\d{1,2}:\d{2}([AP]M)?$/i)) {
					return item.time;
				}
				
				// Tam tarih formatı (dd/mm/yyyy) - parse et
				const dateMatch = item.time.match(/^(\d{1,2})[\/.](\d{1,2})[\/.](\d{4})$/);
				if (dateMatch) {
					const [, day, month, year] = dateMatch;
					const formattedDate = `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
					const timeDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
					const timeDateStr = timeDate.toLocaleDateString('tr-TR');
					
					if (timeDateStr === todayStr) {
						// Bugünkü tarih ama saat bilgisi yok - şu anki saati göster
						const now = new Date();
						const hours = now.getHours().toString().padStart(2, '0');
						const minutes = now.getMinutes().toString().padStart(2, '0');
						return `${hours}:${minutes}`;
					} else {
						// Geçmiş tarih
						return formattedDate;
					}
				}
				
				// Tanımlanamayan format - olduğu gibi döndür
				return item.time;
			}
			
			// Hiçbir alan yoksa boş döndür
			return '';
		} catch (error) {
			console.warn('Tarih formatlama hatası:', error, item);
			return item.time || '';
		}
	}, []);

	// Filter options'ı Redux action ile çek
	useEffect(() => {
		if (selectChatFilterOptionsAction) {
			selectChatFilterOptionsAction();
		}
	}, [selectChatFilterOptionsAction]);

	// Default olarak "Hepsi" seçeneğini ayarla (filterOptions yüklendiğinde)
	useEffect(() => {
		if (filterOptions && filterOptions.length > 0 && selectedFilter === null) {
			setSelectedFilter("all"); // "Hepsi" seçeneği varsayılan olarak seçili
		}
	}, [filterOptions, selectedFilter]);

	/**
	 * Builds select options for conversation activity filtering.
	 * Includes a default "Hepsi" option, and only activity IDs 3 and 5.
	 *
	 * @returns {Array<{value:string|number,label:string}>}
	 */
	const getSelectOptions = useCallback(() => {
		const defaultOptions = [{ value: "all", label: "Hepsi" }];
		
		if (filterOptions && filterOptions.length > 0) {
			// Filter to only include activity IDs 3 and 5
			const filteredOptions = filterOptions
				.filter(item => item.activityid === 3 || item.activityid === 5)
				.map(item => ({
					value: item.activityid,
					label: item.activitydesc
				}));
			
			return [
				...defaultOptions,
				...filteredOptions
			];
		}
		
		// Fallback options (API'den veri gelmezse)
		return defaultOptions;
	}, [filterOptions]);

	/**
	 * Opens a chat by id and resets its unread count.
	 *
	 * @param {string|number} id - Conversation identifier
	 */
	const openChat = useCallback(id => {
		// var lastChar = match.url.substr(match.url.length - 1);
		// var split = "/";
		// if (lastChar == "/") {
		// 	split = "";
		// }
		onChangeChatIndex(id);
		// history.push(`${match.url + split}${id}`)

		resetUnreadCountactions(id);
	}, [onChangeChatIndex, resetUnreadCountactions]);

	/**
	 * Toggles pinning for a conversation by customer number.
	 *
	 * @param {string} customerNumber
	 */
	const handlePinChat = useCallback((customerNumber) => {
		setPinnedChats(prev => prev.includes(customerNumber)
			? prev.filter(num => num !== customerNumber)
			: [...prev, customerNumber]
		);
	}, []);

	const handleActivityStatusChange = useCallback(async (customerId,customerNumber, activityId, activityName) => {
		try {
			
			const params = {
				customerNumber: customerNumber,
				activityId: activityId,
				activityName:activityName,
				customerId:customerId
			};
			
			const result = await changeConversationActivityStatusAction(params);
			
			if (result && result.success) {
				message.success(`Aktivite durumu "${activityName}" olarak değiştirildi`);
			} else {
				message.error(result?.error || 'Aktivite durumu değiştirilemedi');
			}
		} catch (error) {
			message.error('Aktivite durumu değiştirilirken hata oluştu');
		}
	}, [changeConversationActivityStatusAction]);

	const handleStartManualChat = useCallback(async (customerId, customerNumber, customerName) => {
		try {
			const params = {
				customerNumber: customerNumber,
				customerId: customerId
			};
			
			const result = await startManualChatAction(params);
			
			if (result && result.success) {
				message.success(`${customerName} ile manuel sohbet başlatıldı`);
				
				// Aktif conversation'ı sıfırla ki kullanıcı yeniden seçtiğinde güncel bilgilerle yüklensin
				if (onChatListRefresh) {
					onChatListRefresh();
				}
			} else {
				message.error(result?.error || 'Manuel sohbet başlatılamadı');
			}
		} catch (error) {
			message.error('Manuel sohbet başlatılırken hata oluştu');
		}
	}, [startManualChatAction, onChatListRefresh]);

	/**
	 * Builds per-chat dropdown menu including pin/unpin, manual chat, and activity changes.
	 *
	 * @param {Object} item - Chat item
	 * @returns {React.ReactElement} Menu component
	 */
	const getChatMenu = useCallback((item) => {
		const activityMenuItems = [
			<Menu.Item key="pin" onClick={() => handlePinChat(item.customerNumber)}>
				{pinnedChats.includes(item.customerNumber) ? 'Unpinle' : 'Pinle'}
			</Menu.Item>
		];

		// Manuel sohbet başlatma seçeneği - admin transaction aktif değilse ve son transaction 24 saatten eskiyse göster
		if (!item.isAdminTransaction && !item.isLastTransactionOlderThan24Hours) {
			activityMenuItems.push(
				<Menu.Item 
					key="start-manual-chat" 
					onClick={() => handleStartManualChat(item.customerId, item.customerNumber, item.customerName)}
				>
					📞 Manuel Sohbete Başla
				</Menu.Item>
			);
		}

		// Add divider and activity options only for open transactions
		if (filterOptions && filterOptions.length > 0 && item.isOpenTransaction) {
			activityMenuItems.push(<Menu.Divider key="divider" />);
			
			// Add submenu for activity status change
			const activitySubMenuItems = filterOptions.map(activity => (
				<Menu.Item 
					key={`activity-${activity.activityid}`}
					onClick={() => handleActivityStatusChange(item.customerId,item.customerNumber, activity.activityid, activity.activitydesc)}
					disabled={item.activityId === activity.activityid}
				>
					{activity.activitydesc} {item.activityId === activity.activityid ? '✓' : ''}
				</Menu.Item>
			));

			activityMenuItems.push(
				<Menu.SubMenu key="activity-status" title="Aktivite Durumu Değiştir">
					{activitySubMenuItems}
				</Menu.SubMenu>
			);
		}

		return <Menu>{activityMenuItems}</Menu>;
	}, [pinnedChats, filterOptions, handlePinChat, handleStartManualChat, handleActivityStatusChange]);

	/**
	 * Handles search input changes.
	 *
	 * @param {Event} e - Input change event
	 */
	const searchOnChange = e => {
		const query = e.target.value;
		setSearchText(query);
	}

	//const id = parseInt(location.pathname.match(/\/([^/]+)\/?$/)[1])

	// Memoize filtered and sorted chat list to ensure updates propagate
	// Must be called before any early returns (React Hooks rule)
	const newchatList = useMemo(() => {
		let filteredList = JSON.parse(JSON.stringify(chatList || []));
		
		if (searchText.length > 0) {
			filteredList = filteredList.filter(function (el) {
				return (el.customerNumber.toUpperCase().indexOf(searchText.toUpperCase()) !== -1 || (el.customerName || "").toUpperCase().indexOf(searchText.toUpperCase()) !== -1)
			});
		}

		// Pinlenmiş chat'leri listenin başına al
		filteredList.sort((a, b) => {
			const aPinned = pinnedChats.includes(a.customerNumber);
			const bPinned = pinnedChats.includes(b.customerNumber);
			if (aPinned === bPinned) return 0;
			return aPinned ? -1 : 1;
		});

		return filteredList;
	}, [chatList, searchText, pinnedChats]);

	/**
	 * Renders a badge for admin transactions.
	 *
	 * @param {Object} item - Chat item
	 * @returns {JSX.Element|undefined}
	 */
	const getAdminTransactionBadge = (item) => {

		if (item.isAdminTransaction) {

			return (
				<CustomBadge>

				</CustomBadge>
			);
		}
	}

	/**
	 * Renders message list for a given tab key.
	 * Applies admin/open filters and activity filter.
	 *
	 * @param {"1"|"2"} tabKey
	 * @returns {JSX.Element}
	 */
	const getTabMessageList = useCallback((tabKey) => {
		var tabData = JSON.parse(JSON.stringify(newchatList));

		if (tabKey === "2") {
			tabData = tabData.filter(function (el) {
				return (el.isAdminTransaction);
			});

			// Select box filtrelemesi (sadece "Müşteri Temsilcisine bağlananlar" tabında)
			if (selectedFilter !== "all") {
				tabData = tabData.filter(function (el) {
					// Activity ID'ye göre filtreleme

					
					return el.activityId === selectedFilter;
				});
			}
		}

		return (
			<div>
				{/* Aktivite filtresi - sadece Aktif Müşteriler tabında göster */}
				{tabKey === "2" && (
					<div className="chat-menu-activity-filter">
						<Select
							style={{ width: '100%' }}
							placeholder="Aktivite filtresi seçin"
							value={selectedFilter}
							onChange={setSelectedFilter}
							options={getSelectOptions()}
							loading={filterOptionsLoading}
						/>
					</div>
				)}
				<Scrollbars style={{ height: tabKey === "2" ? 'calc(100vh - 390px)' : 'calc(100vh - 350px)' }}>
					<div style={{ padding: '0 10px' }}>
						{tabData.map((item, i) => (
						<div
							style={{
								position: "relative"
							}}
							key={`chat-item-${item.customerNumber}`}
							onClick={() => openChat(item.customerNumber)}
							className={`chat-menu-list-item ${i === (newchatList.length - 1) ? 'last' : ''} ${item.customerNumber == activeNumber ? 'selected' : ''}`}
						>
							{/* İşlem Durumu Belirteci - Hide if chat is ended (isOpenTransaction === false) */}
							{item.isOpenTransaction !== false && (
								<div
									style={{
										position: 'absolute',
										top: 8,
										left: 8,
										width: 12,
										height: 12,
										borderRadius: '50%',
										backgroundColor: item.isAdminTransaction ? '#52c41a' : (item.isOpenTransaction ? '#ff9603' : '#d9d9d9'),
										border: '2px solid #fff',
										boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
										zIndex: 1
									}}
									title={item.isAdminTransaction ? 'Admin Aktif' : (item.isOpenTransaction ? 'Aktif İşlem' : 'Pasif İşlem')}
								/>
							)}
							<AvatarStatus 
								src={globalConstant.imageTypes.getImageUrl(globalConstant.imageTypes.customerImage.type, item.customerImage)}
								name={`${pinnedChats.includes(item.customerNumber) ? '📌 ' : ''}${item.customerName}`} 
								subTitle={
									<div>
										{item.activityId !== 0 && item.activityName && (
											<div style={{ fontSize: '11px', color: '#1890ff', fontWeight: 'bold', marginBottom: '2px' }}>
												🏷️ {item.activityName}
											</div>
										)}
										<div style={{ fontSize: '12px', color: '#666' }}>
											{item.lastWrittenMessage}
										</div>
									</div>
								} 
							/>
							<div className="text-right">
								<div className="chat-menu-list-item-time">{formatChatTime(item)}</div>
								{item.unreadMessageCount === 0 ? <span></span> : <Badge count={item.unreadMessageCount} style={{ backgroundColor: COLOR_1 }} />}
							</div>
							{/* Üç nokta menüsü */}
							<Dropdown overlay={getChatMenu(item)} trigger={['click']} placement="bottomRight">
								<button
									onClick={e => e.stopPropagation()}
									title="Daha fazla"
									style={{
										position: 'absolute',
										top: 8,
										right: 8,
										background: 'none',
										border: 'none',
										cursor: 'pointer',
										fontSize: 18,
										color: '#aaa',
									}}
								>
									<EllipsisOutlined />
								</button>
							</Dropdown>
						</div>
					))}
				</div>
			</Scrollbars>
			</div>
		);
	}, [newchatList, selectedFilter, activeNumber, filterOptions, filterOptionsLoading, pinnedChats, getSelectOptions, openChat, formatChatTime, getChatMenu]);

	// Memoize tab items to ensure they update when chatList or filters change
	// Must be called before any early returns (React Hooks rule)
	const tabItems = useMemo(() => [
		{
			key: "1",
			label: "Tüm Müşteriler",
			children: getTabMessageList("1"),
		},
		{
			key: "2",
			label: "Aktif Müşteriler",
			children: getTabMessageList("2"),
		},
	], [getTabMessageList]);

	if (isLoading) {
		return (
			<Spin tip="Mesajlar Yükleniyor..." style={{ width: "100%", height: "100%", marginTop: 25 }}>
			</Spin>
		);
	}

	/**
	 * Renders chat tabs container with proper items and state.
	 *
	 * @returns {JSX.Element}
	 */
	const getChatTab = () => {
		return (
			<div style={{ marginLeft: "10px", marginRight: "10px", height: '100%' }}>
				
				<Tabs 
					defaultActiveKey={activeTab} 
					onChange={setActiveTab} 
					items={tabItems}
					centered
				>
				</Tabs>
				{/* Select box tabların hemen altında göster */}
				
			</div>
		);
	}

	return (
		<div className="chat-menu">
			<div className="chat-menu-toolbar">
				<Input
					className="chat-menu-search-input"
					placeholder="Kullanıcılarda Ara"
					onChange={searchOnChange}
					prefix={
						<SearchOutlined className="font-size-md mr-2" />
					}
				/>

			</div>
			<div className="chat-menu-list">
				{getChatTab()}
			</div>
		</div>
	)
}

export default ChatMenu
